import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ApiError,
  api,
  type AdminUserCreate,
  type AdminUserUpdate,
  type User,
  type UserRole,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { ToggleSwitch } from '../components/ToggleSwitch';
import { usePersistedTableState } from '../hooks/usePersistedTableState';

const ROLES: UserRole[] = ['admin', 'user', 'viewer'];

const EMPTY_CREATE: AdminUserCreate = {
  username: '',
  password: '',
  role: 'viewer',
  email: null,
  telegram_chat_id: null,
  notify_email_enabled: false,
  notify_telegram_enabled: false,
  notify_on_success: true,
  notify_on_failure: true,
  persist_table_sorting: false,
  persist_table_filters: false,
  show_ui_hints: true,
  ui_theme: 'dark',
  is_active: true,
};

type AdminUsersSortBy = 'username' | 'role' | 'email' | 'notifications' | 'status';

type AdminUsersTableState = {
  sortBy: AdminUsersSortBy;
  sortDir: 'asc' | 'desc';
};

const ADMIN_USERS_TABLE_DEFAULTS: AdminUsersTableState = {
  sortBy: 'username',
  sortDir: 'asc',
};
const ADMIN_USERS_SORTING_KEYS: readonly (keyof AdminUsersTableState)[] = ['sortBy', 'sortDir'];

function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const result = a.localeCompare(b, 'ru');
  return direction === 'asc' ? result : -result;
}

function notificationSummary(user: User): string {
  return [
    user.notify_email_enabled ? 'email' : '',
    user.notify_telegram_enabled ? 'telegram' : '',
    user.notify_on_success ? 'success' : '',
    user.notify_on_failure ? 'failure' : '',
  ]
    .filter(Boolean)
    .join(',');
}

export function AdminUsersPage() {
  const { user } = useAuth();
  if (user && user.role !== 'admin') return <Navigate to="/" replace />;
  return <AdminUsersPanel currentUser={user} />;
}

function AdminUsersPanel({ currentUser }: { currentUser: User | null }) {
  const userId = currentUser?.id;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [createBody, setCreateBody] = useState<AdminUserCreate>(EMPTY_CREATE);
  const [passwords, setPasswords] = useState<Record<string, string>>({});
  const [tableState, setTableState] = usePersistedTableState(
    userId,
    'admin.users',
    ADMIN_USERS_TABLE_DEFAULTS,
    {
      sortingKeys: ADMIN_USERS_SORTING_KEYS,
      persistSorting: Boolean(currentUser?.persist_table_sorting),
      persistFilters: Boolean(currentUser?.persist_table_filters),
    },
  );
  const { sortBy, sortDir } = tableState;

  function updateSort(key: AdminUsersSortBy) {
    if (sortBy === key) {
      setTableState((prev) => ({ ...prev, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' }));
    } else {
      setTableState((prev) => ({ ...prev, sortBy: key, sortDir: 'asc' }));
    }
  }

  function SortHeader({ label, value }: { label: string; value: AdminUsersSortBy }) {
    const active = sortBy === value;
    return (
      <button
        type="button"
        className="text-caption text-text-secondary hover:text-text-primary"
        onClick={() => updateSort(value)}
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    );
  }

  async function loadUsers() {
    setError(null);
    try {
      const data = await api.get<User[]>('/api/admin/users');
      setUsers(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadUsers();
  }, []);

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      await api.post<User>('/api/admin/users', {
        ...createBody,
        email: createBody.email?.trim() || null,
        telegram_chat_id: createBody.telegram_chat_id?.trim() || null,
      });
      setCreateBody({ ...EMPTY_CREATE });
      setMessage('Пользователь создан');
      await loadUsers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function updateUser(id: string, patch: AdminUserUpdate) {
    setError(null);
    setMessage(null);
    try {
      await api.put<User>(`/api/admin/users/${id}`, patch);
      setMessage('Пользователь обновлён');
      await loadUsers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function setPassword(id: string) {
    const password = passwords[id]?.trim();
    if (!password) {
      setError('Введите новый пароль');
      return;
    }
    try {
      await api.put(`/api/admin/users/${id}/password`, { password });
      setPasswords((prev) => ({ ...prev, [id]: '' }));
      setMessage('Пароль назначен');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  if (loading) {
    return <div className="text-text-tertiary">Загрузка пользователей…</div>;
  }

  const sortedUsers = [...users].sort((a, b) => {
    if (sortBy === 'role') return compareText(a.role, b.role, sortDir);
    if (sortBy === 'email') return compareText(a.email, b.email, sortDir);
    if (sortBy === 'notifications') {
      return compareText(notificationSummary(a), notificationSummary(b), sortDir);
    }
    if (sortBy === 'status') {
      return compareText(a.is_active ? 'active' : 'inactive', b.is_active ? 'active' : 'inactive', sortDir);
    }
    return compareText(a.username, b.username, sortDir);
  });

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="rubric">Администрирование</div>
        <h1 className="inline-flex items-center gap-2 text-h1 text-text-primary font-strong">
          Пользователи
          <InfoHint text="Администратор управляет ролями, активностью, уведомлениями и интерфейсными настройками пользователей." />
        </h1>
      </div>

      {message ? <div className="pill-success inline-block">{message}</div> : null}
      {error ? <div className="error-hint">{error}</div> : null}

      <form onSubmit={createUser} className="card card-padding grid md:grid-cols-4 gap-3">
        <input
          className="input"
          placeholder="Логин"
          value={createBody.username}
          onChange={(e) => setCreateBody({ ...createBody, username: e.target.value })}
          minLength={3}
          title="Минимум 3 символа"
          required
        />
        <input
          className="input"
          type="password"
          placeholder="Пароль"
          value={createBody.password}
          onChange={(e) => setCreateBody({ ...createBody, password: e.target.value })}
          minLength={8}
          title="Минимум 8 символов"
          required
        />
        <select
          className="input"
          value={createBody.role}
          onChange={(e) => setCreateBody({ ...createBody, role: e.target.value as UserRole })}
        >
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary btn-md">
          Создать
        </button>
      </form>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th><SortHeader label="Логин" value="username" /></th>
              <th><SortHeader label="Роль" value="role" /></th>
              <th><SortHeader label="Контакты" value="email" /></th>
              <th><SortHeader label="Уведомления" value="notifications" /></th>
              <th>Интерфейс</th>
              <th><SortHeader label="Статус" value="status" /></th>
              <th>Пароль</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((item) => {
              const updateBody: AdminUserUpdate = {
                role: item.role,
                email: item.email,
                telegram_chat_id: item.telegram_chat_id,
                notify_email_enabled: item.notify_email_enabled,
                notify_telegram_enabled: item.notify_telegram_enabled,
                notify_on_success: item.notify_on_success,
                notify_on_failure: item.notify_on_failure,
                persist_table_sorting: item.persist_table_sorting,
                persist_table_filters: item.persist_table_filters,
                show_ui_hints: item.show_ui_hints,
                ui_theme: item.ui_theme,
                is_active: item.is_active,
              };
              return (
                <tr key={item.id}>
                  <td className="text-text-primary">{item.username}</td>
                  <td>
                    <select
                      className="input input-sm min-w-[110px]"
                      value={item.role}
                      onChange={(e) =>
                        updateUser(item.id, { ...updateBody, role: e.target.value as UserRole })
                      }
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="min-w-[220px] space-y-2">
                    <input
                      className="input input-sm"
                      value={item.email ?? ''}
                      placeholder="email"
                      onBlur={(e) =>
                        updateUser(item.id, {
                          ...updateBody,
                          email: e.target.value.trim() || null,
                        })
                      }
                      onChange={(e) => {
                        setUsers((prev) =>
                          prev.map((u) => (u.id === item.id ? { ...u, email: e.target.value } : u)),
                        );
                      }}
                    />
                    <input
                      className="input input-sm"
                      value={item.telegram_chat_id ?? ''}
                      placeholder="telegram"
                      onBlur={(e) =>
                        updateUser(item.id, {
                          ...updateBody,
                          telegram_chat_id: e.target.value.trim() || null,
                        })
                      }
                      onChange={(e) => {
                        setUsers((prev) =>
                          prev.map((u) =>
                            u.id === item.id ? { ...u, telegram_chat_id: e.target.value } : u,
                          ),
                        );
                      }}
                    />
                  </td>
                  <td className="space-y-1">
                    {[
                      ['notify_email_enabled', 'Email'],
                      ['notify_telegram_enabled', 'Telegram'],
                      ['notify_on_success', 'Success'],
                      ['notify_on_failure', 'Failure'],
                    ].map(([key, label]) => (
                      <ToggleSwitch
                        key={key}
                        checked={Boolean(item[key as keyof User])}
                        onChange={(checked) =>
                          updateUser(item.id, {
                            ...updateBody,
                            [key]: checked,
                          } as AdminUserUpdate)
                        }
                        label={label}
                        className="border-transparent bg-transparent px-0 py-1"
                      />
                    ))}
                  </td>
                  <td>
                    <ToggleSwitch
                      checked={item.show_ui_hints}
                      onChange={(checked) =>
                        updateUser(item.id, { ...updateBody, show_ui_hints: checked })
                      }
                      label="Подсказки"
                      className="min-w-[150px]"
                    />
                  </td>
                  <td>
                    <ToggleSwitch
                      checked={item.is_active}
                      onChange={(checked) =>
                        updateUser(item.id, { ...updateBody, is_active: checked })
                      }
                      label={item.is_active ? 'active' : 'inactive'}
                      className="min-w-[150px]"
                    />
                  </td>
                  <td className="min-w-[180px]">
                    <input
                      className="input input-sm"
                      type="password"
                      placeholder="Новый пароль"
                      value={passwords[item.id] ?? ''}
                      minLength={8}
                      title="Минимум 8 символов"
                      onChange={(e) =>
                        setPasswords((prev) => ({ ...prev, [item.id]: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      className="btn-subtle btn-sm mt-1"
                      onClick={() => void setPassword(item.id)}
                      >
                        Назначить
                      </button>
                    </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
